import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Virtuoso } from "react-virtuoso";
import { useFontStyle } from "~/hook/useFontStyle";
import { useLanguageParams } from "~/hook/useLanguageParams";
import { DailyDarshanPhotosProps, DailyImagecardProps } from "~/interface/daily-darshan/daily-darshan";
import { getEventImageDate } from "~/lib/helperFuncation/formateDate";
import { cn } from "~/lib/utils";
import { BLUR_HASH, FALLBACK_IMAGE_URL } from "~/utils/constant";
import ClientOnly from "../shared-components/ClentOnly";
import LoadingSpinner from "../shared-components/LoadingSpinner";
import NoHasMoreText from "../shared-components/NoHasMoreText";
import RowGallery from "../shared-components/RowGallery";

const DailyDarshanPhotos = ({ mandirImages, dateArray, onLoadMore, hasMore = false, loading = false, selectedDham }: DailyDarshanPhotosProps) => {
  const [width, setWidth] = useState<number>(0);
  const lang = useLanguageParams();
  const { t } = useTranslation();
  const { medium } = useFontStyle();

  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    handleResize(); // Set initial width
    window.addEventListener("resize", handleResize); // Update on window resize

    window.removeEventListener("resize", handleResize);
  }, []);

  const sortedDates = useMemo(() => dateArray, [dateArray]);

  return (
    <ClientOnly
      fallback={
        <div className="w-full">
          {sortedDates?.map((date, i) => (
            <React.Fragment key={date}>
              <DailyImagecard date={date} mandirImages={mandirImages} rowHeight={280} selectedDham={selectedDham} />
            </React.Fragment>
          ))}
          {/* <span>Loading....</span> */}
        </div>
      }
    >
      <Virtuoso
        style={{ height: "100%", width: "100%" }}
        data={sortedDates ? sortedDates : []}
        endReached={onLoadMore}
        increaseViewportBy={200}
        useWindowScroll
        itemContent={(i, date) => {
          return <DailyImagecard date={date} mandirImages={mandirImages} rowHeight={width <= 425 ? 180 : 280} selectedDham={selectedDham} />;
        }}
        components={{
          Footer: () => {
            return loading || hasMore ? (
              <LoadingSpinner />
            ) : (
              <NoHasMoreText className="text-textSecondaryColor" text={t("common.seeAllData", { data: t("daily-darshan.photos") })} />
            );
          },
        }}
      />
    </ClientOnly>
  );
};

const DailyImagecard = ({ date, mandirImages, rowHeight, selectedDham }: DailyImagecardProps) => {
  const { t } = useTranslation();
  const { medium } = useFontStyle();
  const { month, day, year } = getEventImageDate(date);
  const formattedDate = `${day} ${t(`common.${month.toLowerCase()}`)}, ${year}`;

  return (
    <>
      <div key={date} className="w-full relative md:px-4 pb-5 md:mx-2">
        <div className="h-full w-[3px] hidden md:block absolute left-0 top-2 bg-eventcardGradient opacity-100" />
        <div className="size-[10px] hidden md:block rounded-full absolute top-2 left-[-3px] bg-textColor" />
        <div className="flex flex-col justify-start items-start gap-1 md:pl-3">
          <p className={cn(" text-textColor body_text_l", medium)} title={date}>
            {formattedDate}
          </p>
          <div className="flex justify-start items-start w-full">
            <div className="w-full">
              {Array.isArray(mandirImages[date]) && (
                <RowGallery
                  imageData={mandirImages[date]?.map((item) => ({
                    id: item.id,
                    url: item.img
                      ? {
                          small: item.img?.s.url,
                          medium: item.img?.m.url,
                          large: item.img?.l.url,
                          hd: item.img?.hd?.url,
                          original: item.img?.og.url,
                        }
                      : {
                          small: FALLBACK_IMAGE_URL,
                          medium: FALLBACK_IMAGE_URL,
                          large: FALLBACK_IMAGE_URL,
                          hd: FALLBACK_IMAGE_URL,
                          original: FALLBACK_IMAGE_URL,
                        },
                    alt: `${selectedDham} ${item.date}`,
                    blur_hash: item.img ? item.img.bh : BLUR_HASH,
                    width: item.img ? item.img.ar * rowHeight : 200,
                    height: rowHeight,
                    video_id: item.type === "yt" ? item.original_url : null,
                  }))}
                  rowHeight={rowHeight}
                  className="flex-wrap"
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DailyDarshanPhotos;
